import React, { useState } from "react";
import {
  Box, Typography, InputBase, IconButton, Tooltip,
} from "@mui/material";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import { useTheme } from "@mui/material/styles";

// ─── Types ────────────────────────────────────────────────────────────────────
interface Company {
  id: number;
  name: string;
  subtitle: string;
}

// ─── Company Row ──────────────────────────────────────────────────────────────
const CompanyRow: React.FC<{
  company: Company;
  onEdit: (id: number) => void;
  onDelete: (id: number) => void;
}> = ({ company, onEdit, onDelete }) => {
  const theme = useTheme();
  const [hovered, setHovered] = useState(false);

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        px: 2.5, py: 1.5, mb: "2px",
        backgroundColor: hovered ? theme.custom_colors.cardHover : theme.palette.background.paper,
        borderRadius: "6px", cursor: "pointer",
        transition: "background-color 0.15s ease",
      }}
    >
      <Box>
        <Typography variant="body1" sx={{ color: theme.palette.text.primary, fontWeight: 500, lineHeight: 1.4 }}>
          {company.name}
        </Typography>
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
          {company.subtitle}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", gap: 0.5, opacity: hovered ? 1 : 0.4, transition: "opacity 0.15s" }}>
        <Tooltip title="Manage Users">
          <IconButton size="small" sx={{ color: theme.palette.text.disabled, "&:hover": { color: theme.palette.primary.main } }}>
            <ManageAccountsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Edit">
          <IconButton size="small" onClick={() => onEdit(company.id)}
            sx={{ color: theme.palette.text.disabled, "&:hover": { color: theme.palette.text.primary } }}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton size="small" onClick={() => onDelete(company.id)}
            sx={{ color: theme.palette.text.disabled, "&:hover": { color: theme.palette.error.main } }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

// ─── Company View ─────────────────────────────────────────────────────────────
const CompanyView: React.FC = () => {
  const theme = useTheme();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [search, setSearch]       = useState("");

  const filtered = companies.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleDelete = (id: number) =>
    setCompanies(prev => prev.filter(c => c.id !== id));

  const handleEdit = (id: number) => alert(`Edit company #${id}`);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* Search */}
      <Box sx={{
        display: "flex", alignItems: "center", gap: 1,
        mx: 2, mt: 2, mb: 1.5, px: 2, py: 1,
        backgroundColor: theme.palette.background.paper,
        borderRadius: "6px", border: `1px solid ${theme.palette.divider}`,
      }}>
        <SearchIcon sx={{ color: theme.palette.text.disabled, fontSize: "1.1rem" }} />
        <InputBase
          placeholder="Search Company..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{
            flex: 1, color: theme.palette.text.primary, fontSize: "0.875rem",
            "& input::placeholder": { color: theme.palette.text.disabled, opacity: 1 },
          }}
        />
      </Box>

      {/* List */}
      <Box sx={{ flex: 1, overflowY: "auto", px: 1.5, pb: 2 }}>
        {filtered.length > 0 ? (
          filtered.map(company => (
            <CompanyRow
              key={company.id}
              company={company}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))
        ) : (
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, textAlign: "center", mt: 6 }}>
            No companies found.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default CompanyView;